/** @jsxImportSource @emotion/react */

import React, { useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { FieldArray } from 'react-final-form-arrays'
import { Button, Modal, Typography } from '@mui/material'
import { validateRequired, validateRequiredHtml } from 'utils/validation'
import { EditorField } from 'components/Editor'
import { InputField } from 'components/Input'
import { Revision, Revisions } from 'components/Revisions'
import { validateVideoLink } from 'pagesNew/ResponseTests/Form/utils'
import { findOneExtensionExamSection_findOneExtensionExamSection_extensionQuestion as Question } from '__generatedTypes__/findOneExtensionExamSection'
import { getAnswerLetterLabel } from 'utils/getAnswerLetterLabel'
import * as classes from './styles'
import { QuestionPreview } from './QuestionPreview'

export type QuestionWithIndex = Question & { questionIndex: number }

const focusOnErrors = createDecorator<QuestionWithIndex, Partial<QuestionWithIndex>>()

export type QuestionEditModalProps = {
  isOpen: boolean
  question: QuestionWithIndex | null
  onSubmit: (question: QuestionWithIndex) => void
  onClose: () => void
}

export const QuestionEditModal = ({ isOpen, question, onSubmit, onClose }: QuestionEditModalProps) => {
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(true)
  const initialValues: Question = useMemo(
    () => ({
      ...(question as Question),
      answerSolution: question?.answerSolution?.length
        ? question.answerSolution
        : Array.from({ length: question?.optionsCount ?? 0 }),
    }),
    [question]
  )

  // added disableEnforceFocus sx={{ zIndex: 100 }} to fix an issue with editor tooltips
  return (
    <Modal open={isOpen} onClose={onClose} disableEnforceFocus sx={{ zIndex: 100 }}>
      <div css={classes.modalContainerWrapper} onClick={onClose}>
        <div css={classes.modalContainer} onClick={(e) => e.stopPropagation()}>
          <Form
            mutators={{ ...arrayMutators }}
            decorators={[focusOnErrors]}
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit, submitting }) => (
              <form css={classes.modalForm} onSubmit={handleSubmit} data-testid="edit-question-solutions-modal">
                <Typography css={classes.modalTitle} variant="title2">
                  Update Question #{question?.questionIndex}
                </Typography>

                {question && (
                  <QuestionPreview
                    questionIndex={question.questionIndex}
                    question={question}
                    expanded={isQuestionExpanded}
                    onExpandClick={() => setIsQuestionExpanded((prev) => !prev)}
                    onEditClick={() => null}
                    isQuestionOnly
                  />
                )}

                <EditorField
                  type="extensionSolution"
                  name="mainSolution"
                  label="Main Solution"
                  validate={validateRequiredHtml}
                  placeholder="Enter Main Solution"
                  data-testid="main-solution-field"
                />
                <InputField
                  label="Main Solution Video (Optional)"
                  name="mainVideoSolution"
                  validate={validateVideoLink}
                  placeholder="Enter Main Solution Video Link"
                  fullWidth
                />

                <FieldArray name="answerSolution" validate={validateRequired}>
                  {({ fields }) => (
                    <>
                      {fields.map((field, index) => {
                        const answerLabel = getAnswerLetterLabel(index)
                        const isCorrect = answerLabel === question?.correctOption
                        return (
                          <EditorField
                            key={index}
                            type="extensionSolution"
                            name={field}
                            label={`Solution for Option ${answerLabel}${isCorrect ? ' (correct)' : ''}`}
                            validate={validateRequiredHtml}
                            placeholder={`Enter Solution for Option ${answerLabel}${isCorrect ? ' (correct)' : ''}`}
                            data-testid={`answer-${index}-solution-field`}
                          />
                        )
                      })}
                    </>
                  )}
                </FieldArray>

                <Button variant="outlined" color="blue" size="large" disabled={submitting} onClick={onClose} fullWidth>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" color="blue" size="large" disabled={submitting} fullWidth>
                  Save
                </Button>

                {!!initialValues.history?.length && (
                  <Revisions
                    revisions={(initialValues?.history ?? []) as Revision[]}
                    css={classes.history}
                    hideTitle
                    editOnly
                  />
                )}
              </form>
            )}
          />
        </div>
      </div>
    </Modal>
  )
}
